import { useState, useEffect, useCallback, Fragment } from "react";
import { Search, History } from "lucide-react";
import api from "../lib/api";
import { SectionCard, Loading, Empty, Pill } from "./kit";
import { Input, Select } from "./form";
import { titleCase, fmtStamp, timeAgo } from "../lib/constants";

const ACTION_COLOR = {
  created: "bg-emerald-50 text-emerald-700 border-emerald-200",
  updated: "bg-sky-50 text-sky-700 border-sky-200",
  deleted: "bg-red-50 text-red-600 border-red-200",
  login: "bg-slate-50 text-slate-500 border-slate-200",
};

function details(a) {
  const nv = a.new_value, pv = a.previous_value;
  if (nv && typeof nv === "object" && !Array.isArray(nv)) {
    const parts = [];
    if (nv.name) parts.push(nv.name);
    if (nv.email) parts.push(nv.email);
    if (nv.role) parts.push(`role: ${nv.role}`);
    if (Array.isArray(nv.fields)) parts.push(`fields: ${nv.fields.join(", ")}`);
    if (nv.imported != null) parts.push(`${nv.imported} imported, ${nv.duplicates} duplicates`);
    if (parts.length) return parts.join(" · ");
  }
  if (Array.isArray(nv) && nv.length) return `fields: ${nv.filter((k) => !["updated_at", "updated_by", "updated_by_name"].includes(k)).join(", ")}`;
  if (pv && typeof pv === "object") return pv.name || pv.merged || pv.text || "";
  return "";
}

function DiffView({ a }) {
  const pv = a.previous_value && typeof a.previous_value === "object" && !Array.isArray(a.previous_value) ? a.previous_value : {};
  const nv = a.new_value && typeof a.new_value === "object" && !Array.isArray(a.new_value) ? a.new_value : {};
  const keys = [...new Set([...Object.keys(pv), ...Object.keys(nv)])].filter((k) => k !== "name" && JSON.stringify(pv[k]) !== JSON.stringify(nv[k]));
  const fmt = (v) => (v === undefined || v === null || v === "" ? <span className="text-slate-300">—</span> : typeof v === "object" ? <code className="text-[11px]">{JSON.stringify(v)}</code> : String(v));
  if (keys.length === 0) return <p className="text-xs text-slate-400 py-2">No field-level changes recorded for this entry{Array.isArray(a.new_value) ? ` (fields: ${a.new_value.join(", ")})` : ""}.</p>;
  return (
    <table className="w-full text-xs my-2" data-testid={`audit-diff-${a.id}`}>
      <thead><tr className="text-slate-400 uppercase tracking-wide text-[10px]"><th className="text-left py-1 pr-3">Field</th><th className="text-left py-1 pr-3">Old value</th><th className="text-left py-1">New value</th></tr></thead>
      <tbody>{keys.map((k) => (
        <tr key={k} className="border-t border-slate-100"><td className="py-1.5 pr-3 font-medium text-navy-800">{titleCase(k)}</td>
          <td className="py-1.5 pr-3 text-red-600 bg-red-50/60 line-through decoration-red-300">{fmt(pv[k])}</td><td className="py-1.5 text-emerald-700 bg-emerald-50/60">{fmt(nv[k])}</td></tr>
      ))}</tbody>
    </table>
  );
}

export default function AuditLog() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ limit: "300" });
    if (search) p.set("search", search);
    if (action) p.set("action", action);
    const { data } = await api.get(`/audit-logs?${p.toString()}`);
    setData(data);
  }, [search, action]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <SectionCard title="Audit Log" testid="audit-log-card"
      action={data && <span className="text-xs text-slate-400">{data.total} entries</span>}>
      <p className="text-sm text-slate-500 mb-4">Every create, update, delete, upload, and sign-in — who did it and exactly when.</p>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by person, record, or action…" className="pl-9" data-testid="audit-search" /></div>
        <Select value={action} onChange={setAction} testid="audit-action-filter"
          options={[{ value: "", label: "All actions" }, ...["created", "updated", "deleted", "note_created", "note_edited", "import", "listing_imported", "sync_executed", "source_connected", "source_disconnected", "mapping_changed", "permission_changed", "source_value_overridden", "property_merged", "batch_purged", "contact_merged", "settings_updated", "user_created", "user_updated", "profile_updated", "sequence_enrolled", "login"].map((a) => ({ value: a, label: titleCase(a) }))]} />
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <Empty title="No matching activity" /> : (
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm min-w-[720px]" data-testid="audit-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
              <tr><th className="text-left px-5 py-2.5">When</th><th className="text-left px-4 py-2.5">Who</th><th className="text-left px-4 py-2.5">Action</th><th className="text-left px-4 py-2.5">Record</th><th className="text-left px-4 py-2.5">Details</th></tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <Fragment key={a.id}>
                <tr className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${open === a.id ? "bg-amber-50/40" : ""}`} onClick={() => setOpen(open === a.id ? null : a.id)} data-testid={`audit-row-${a.id}`}>
                  <td className="px-5 py-2.5 whitespace-nowrap"><p className="text-navy-800">{fmtStamp(a.timestamp)}</p><p className="text-[11px] text-slate-400">{timeAgo(a.timestamp)}</p></td>
                  <td className="px-4 py-2.5 font-medium text-navy-800 whitespace-nowrap">{a.user_name || "System"}</td>
                  <td className="px-4 py-2.5"><Pill className={`text-[10px] ${ACTION_COLOR[a.action] || ""}`}>{titleCase(a.action)}</Pill></td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{titleCase((a.record_type || "").replace(/s$/, ""))}</td>
                  <td className="px-4 py-2.5 text-slate-500 max-w-[320px] truncate" title={details(a)}>{details(a) || <span className="text-slate-300 flex items-center gap-1"><History className="w-3 h-3" />{(a.record_id || "").slice(0, 8)}</span>}</td>
                </tr>
                {open === a.id && <tr className="bg-amber-50/30"><td colSpan={5} className="px-5 pb-3"><DiffView a={a} /></td></tr>}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
